/**
 * Remembers how someone last signed in on this device, so the login and
 * signup screens can put a "Last used" tag on Google or email (2026-10-16).
 * A lot of "no account found" errors are people who signed up with Google
 * and came back typing their email, or the other way round.
 *
 * Only the method is kept, never the email address. This is a health
 * product and computers get shared, so we don't leave someone's email
 * sitting on the login page for the next person.
 *
 * It's set in two steps: "pending" when they start (Google click or code
 * sent), and moved to "last" once AppGate sees a real session. That way a
 * Google attempt they backed out of doesn't get the tag.
 */

export type SignInMethod = 'google' | 'email';

const PENDING_KEY = 'ml_signin_pending';
const LAST_KEY = 'ml_signin_last';

export function markSignInStarted(method: SignInMethod): void {
  try {
    localStorage.setItem(PENDING_KEY, method);
  } catch {
    /* no localStorage, the tag just won't show */
  }
}

/** Call once a session exists. */
export function confirmSignIn(): void {
  try {
    const pending = localStorage.getItem(PENDING_KEY);
    if (pending === 'google' || pending === 'email') {
      localStorage.setItem(LAST_KEY, pending);
    }
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* nothing to do */
  }
}

export function getLastSignIn(): SignInMethod | null {
  try {
    const v = localStorage.getItem(LAST_KEY);
    return v === 'google' || v === 'email' ? v : null;
  } catch {
    return null;
  }
}
